import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faPlus, faSearch, faFolder, faChevronDown, faChevronUp, faCheck, faArrowLeft, faTrash, faThumbtack } from '@fortawesome/free-solid-svg-icons';
import { api, getApiUrl } from '../utils/apiConfig';
import { getFolderDisplayLabel } from '../utils/folderHierarchy';
import { useAccount } from '../contexts/AccountContext';
import { getUserData } from '../utils/userUtils';
import { useDebouncedValue } from '../hooks/useDebouncedValue';
import DataTable, { createActionColumn } from './DataTable';
import StatusBadge from './StatusBadge';
import './Pages.css';

/**
 * PacingPage component for managing pacing schedules
 * Displays all pacing schedules for the current account
 * Aligned with CampaignsPage layout and functionality
 */
const PacingPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccountIdToUse } = useAccount();
  const [user] = useState(() => getUserData());
  const accountId = getAccountIdToUse();

  const [pacingSchedules, setPacingSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Folder filter state
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [foldersLoading, setFoldersLoading] = useState(false);

  // Helper function to get persisted pagination state
  const getPersistedPaginationState = () => {
    const urlFolderId = searchParams.get('folderId');
    try {
      const saved = sessionStorage.getItem('pacingPage_paginationState');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (urlFolderId) {
          parsed.selectedFolderId = urlFolderId;
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading pagination state:', error);
    }
    return {
      currentPage: 1,
      itemsPerPage: 10,
      sortBy: 'created_at',
      sortOrder: 'desc',
      searchTerm: '',
      selectedFolderId: urlFolderId || null
    };
  };

  const initialState = getPersistedPaginationState();

  // Pagination and sorting state
  const [currentPage, setCurrentPage] = useState(initialState.currentPage);
  const [itemsPerPage, setItemsPerPage] = useState(initialState.itemsPerPage);
  const [sortBy, setSortBy] = useState(initialState.sortBy);
  const [sortOrder, setSortOrder] = useState(initialState.sortOrder);
  const [searchTerm, setSearchTerm] = useState(initialState.searchTerm);
  // Search-as-you-type against GET /api/v2/pacing — debounced through the
  // shared typeahead hook so a burst of keystrokes fetches only the final
  // term. The raw searchTerm still persists to sessionStorage instantly.
  const debouncedSearchTerm = useDebouncedValue(searchTerm);
  const [selectedFolderId, setSelectedFolderId] = useState(initialState.selectedFolderId);
  const [pagination, setPagination] = useState({
    currentPage: initialState.currentPage,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: initialState.itemsPerPage,
    hasNextPage: false,
    hasPrevPage: false
  });

  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const dropdownRefs = useRef({});

  // Edit pacing name inline state
  const [editingPacingId, setEditingPacingId] = useState(null);
  const [editingPacingName, setEditingPacingName] = useState('');
  const [editingPacingType, setEditingPacingType] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  // Helper function to persist pagination state
  const persistPaginationState = (state) => {
    try {
      sessionStorage.setItem('pacingPage_paginationState', JSON.stringify(state));
    } catch (error) {
      console.error('Error saving pagination state:', error);
    }
  };

  // Persist pagination state whenever it changes
  useEffect(() => {
    const state = {
      currentPage,
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm,
      selectedFolderId
    };
    persistPaginationState(state);
  }, [currentPage, itemsPerPage, sortBy, sortOrder, searchTerm, selectedFolderId]);

  // Fetch folders when account changes
  useEffect(() => {
    if (accountId) {
      fetchFolders();
    }
  }, [accountId]);

  // Fetch pacing schedules when filters change (search rides the debounced
  // term, so the fetch fires once the user stops typing)
  useEffect(() => {
    if (accountId) {
      fetchPacingSchedules();
    }
  }, [accountId, currentPage, itemsPerPage, sortBy, sortOrder, debouncedSearchTerm, selectedFolderId]);

  const fetchPacingSchedules = async () => {
    if (!accountId) return;

    try {
      setIsLoading(true);
      setError(null);

      // The list interleaves manual and auto schedules, so pagination must be
      // applied to the MERGED list. Fetch both lists in full (the bulk endpoint
      // has no pagination), then filter/sort/slice client-side — otherwise the
      // server-paginated manual page gets ALL auto schedules appended to every
      // screen.
      const params = {
        accountId,
        userId: user?.userId || user?.id,
        search: debouncedSearchTerm,
        sortBy,
        sortOrder,
        page: 1,
        limit: 500
      };

      // Add folderId filter if a folder is selected
      if (selectedFolderId) {
        params.folderId = selectedFolderId;
      }

      // Fetch both manual pacing and bulk auto-pacing schedules in parallel
      const [manualResponse, bulkResponse] = await Promise.all([
        api.get(getApiUrl('/api/v2/pacing'), { params }),
        api.get(getApiUrl('/api/v2/bulk-auto-pacing'), { params: { accountId, userId: user?.userId || user?.id } })
      ]);

      // Process manual pacing schedules
      const manualSchedules = (manualResponse.data.pacingSchedules || []).map(s => ({
        ...s,
        pacingType: 'manual'
      }));

      // Process bulk auto-pacing schedules
      const bulkSchedules = (bulkResponse.data.schedules || []).map(s => ({
        ...s,
        id: s.id,
        name: s.name,
        platform: s.platform,
        status: s.status,
        totalCampaigns: parseInt(s.campaign_count) || s.total_campaigns || 0,
        successfulCampaigns: parseInt(s.successful_campaigns) || 0,
        failedCampaigns: parseInt(s.failed_campaigns) || 0,
        folderId: s.folder_id,
        folderName: s.folder_name,
        createdAt: s.created_at,
        created_at: s.created_at,
        is_pinned: s.is_pinned,
        pacingType: 'auto'
      }));

      // Combine and sort all schedules
      let allSchedules = [...manualSchedules, ...bulkSchedules];

      // The manual endpoint filters by folder server-side; apply the same
      // filter to the bulk schedules client-side.
      if (selectedFolderId) {
        allSchedules = allSchedules.filter(s =>
          s.pacingType === 'manual' || s.folderId === selectedFolderId
        );
      }

      // Apply search filter client-side for bulk schedules
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        allSchedules = allSchedules.filter(s =>
          s.name?.toLowerCase().includes(searchLower) ||
          s.platform?.toLowerCase().includes(searchLower)
        );
      }

      // Apply client-side sorting for combined results
      allSchedules.sort((a, b) => {
        // Pinned items first
        if (a.is_pinned && !b.is_pinned) return -1;
        if (!a.is_pinned && b.is_pinned) return 1;

        let comparison = 0;
        if (sortBy === 'name') {
          comparison = (a.name || '').localeCompare(b.name || '');
        } else if (sortBy === 'status') {
          comparison = (a.status || '').localeCompare(b.status || '');
        } else if (sortBy === 'platform') {
          comparison = (a.platform || '').localeCompare(b.platform || '');
        } else if (sortBy === 'folder_name') {
          comparison = (a.folderName || '').localeCompare(b.folderName || '');
        } else if (sortBy === 'created_at') {
          comparison = new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at);
        }
        return sortOrder === 'asc' ? comparison : -comparison;
      });

      // Paginate the merged list client-side
      const totalItems = allSchedules.length;
      const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
      const effectivePage = Math.min(currentPage, totalPages);
      const pageStart = (effectivePage - 1) * itemsPerPage;
      setPacingSchedules(allSchedules.slice(pageStart, pageStart + itemsPerPage));

      setPagination({
        currentPage: effectivePage,
        totalPages,
        totalItems,
        itemsPerPage,
        hasNextPage: effectivePage < totalPages,
        hasPrevPage: effectivePage > 1
      });
    } catch (err) {
      console.error('Error fetching pacing schedules:', err);
      setError(err.response?.data?.error || 'Failed to load pacing schedules');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    if (!accountId) return;

    try {
      setFoldersLoading(true);
      const response = await api.get(getApiUrl('/api/v2/folders'), {
        params: {
          accountId,
          userId: user?.userId || user?.id,
          limit: 100
        }
      });

      const foldersData = response.data.folders || response.data || [];
      setFolders(foldersData);

      // If we have a selectedFolderId but no selectedFolder object, find and set it
      if (selectedFolderId && !selectedFolder) {
        const folder = foldersData.find(f => f.id === selectedFolderId);
        if (folder) {
          setSelectedFolder(folder);
        } else {
          setSelectedFolderId(null);
          setSelectedFolder(null);
        }
      }
    } catch (err) {
      console.error('Error fetching folders:', err);
    } finally {
      setFoldersLoading(false);
    }
  };

  // The fetch keys off `debouncedSearchTerm`; this just updates the input and
  // resets to page 1.
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Handle edit pacing name
  const handleEditPacing = (schedule) => {
    setEditingPacingId(schedule.id);
    setEditingPacingName(schedule.name);
    setEditingPacingType(schedule.pacingType);
    setError(null);
  };

  const handleUpdatePacing = async () => {
    if (!editingPacingName.trim()) return;

    try {
      setIsUpdating(true);
      setError(null);

      // Use correct endpoint based on pacing type
      const endpoint = editingPacingType === 'auto'
        ? `/api/v2/bulk-auto-pacing/${editingPacingId}`
        : `/api/v2/pacing/${editingPacingId}`;

      const response = await api.patch(getApiUrl(endpoint), {
        name: editingPacingName.trim()
      }, {
        params: { accountId, userId: user?.userId || user?.id }
      });

      if (response.status === 200) {
        // Update the pacing schedule in local state
        setPacingSchedules(prev => prev.map(s =>
          s.id === editingPacingId
            ? { ...s, name: editingPacingName.trim() }
            : s
        ));

        // Exit edit mode
        setEditingPacingId(null);
        setEditingPacingName('');
        setEditingPacingType(null);
      }
    } catch (err) {
      console.error('Error updating pacing schedule:', err);
      const errorMessage = err.response?.data?.error || 'Failed to update pacing schedule';
      setError(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingPacingId(null);
    setEditingPacingName('');
    setEditingPacingType(null);
    setError(null);
  };

  const requestSort = (key) => {
    let direction = 'desc';
    if (sortBy === key && sortOrder === 'desc') {
      direction = 'asc';
    }
    setSortBy(key);
    setSortOrder(direction);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  // Handle folder filter
  const handleFolderSelect = (folder) => {
    if (folder) {
      setSelectedFolder(folder);
      setSelectedFolderId(folder.id);
      setSearchParams({ folderId: folder.id });
    } else {
      setSelectedFolder(null);
      setSelectedFolderId(null);
      setSearchParams({});
    }
    setCurrentPage(1);
    setShowFolderDropdown(false);
  };

  // Handle dropdown toggle
  const toggleDropdown = (scheduleId, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (openDropdownId === scheduleId) {
      setOpenDropdownId(null);
      return;
    }

    const buttonRect = e.currentTarget.getBoundingClientRect();
    const dropdownWidth = 160;
    const dropdownHeight = 80;

    let top = buttonRect.bottom + 5;
    let left = buttonRect.right - dropdownWidth;

    if (left < 10) {
      left = 10;
    }

    if (top + dropdownHeight > window.innerHeight - 10) {
      top = buttonRect.top - dropdownHeight - 5;
    }

    setDropdownPosition({ top, left });
    setOpenDropdownId(scheduleId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openDropdownId && !Object.values(dropdownRefs.current).some(ref =>
        ref && ref.contains(event.target)
      )) {
        setOpenDropdownId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openDropdownId]);

  // Close folder dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showFolderDropdown && !event.target.closest('.folder-filter-container')) {
        setShowFolderDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showFolderDropdown]);

  // Format date helper - aligned with CampaignsPage
  const formatDate = (schedule) => {
    const date = schedule.createdAt || schedule.created_at;
    if (!date) return 'Unknown';

    const scheduleDate = new Date(date);
    const now = new Date();

    const isToday = scheduleDate.toDateString() === now.toDateString();

    const yesterday = new Date(now);
    yesterday.setDate(yesterday.getDate() - 1);
    const isYesterday = scheduleDate.toDateString() === yesterday.toDateString();

    if (isToday) {
      return `Today ${scheduleDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else if (isYesterday) {
      return `Yesterday ${scheduleDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}`;
    } else {
      const diffTime = Math.abs(now - scheduleDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays <= 7) {
        return `${diffDays} days ago`;
      } else {
        return scheduleDate.toLocaleDateString();
      }
    }
  };

  // Platform display name
  const getPlatformName = (platform) => {
    const names = {
      'dv360': 'DV360',
      'ttd': 'The Trade Desk',
      'facebook': 'Facebook',
      'meta': 'Meta',
      'google_search': 'Google Ads',
      'google-ads-search': 'Google Ads Search',
      'google_ads': 'Google Ads Search',
      'google_ads_search': 'Google Ads Search',
      'tiktok': 'TikTok',
      'reddit': 'Reddit',
      'linkedin': 'LinkedIn',
      'madhive': 'MadHive'
    };
    // Try direct match first, then lowercase match
    return names[platform] || names[platform?.toLowerCase()] || 'Unknown';
  };

  // Table columns - aligned with CampaignsPage
  const columns = [
    {
      key: 'name',
      label: 'Name',
      width: '1fr',
      sortable: true,
      resizable: true,
      render: (schedule) => (
        <div className="campaign-name-content">
          {schedule.is_pinned && (
            <FontAwesomeIcon
              icon={faThumbtack}
              className="pinned-icon"
              title="Pinned pacing schedule"
            />
          )}
          {editingPacingId === schedule.id ? (
            <div
              className="inline-edit-container"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                type="text"
                value={editingPacingName}
                onChange={(e) => setEditingPacingName(e.target.value)}
                onClick={(e) => e.stopPropagation()}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleUpdatePacing();
                  } else if (e.key === 'Escape') {
                    handleCancelEdit();
                  }
                }}
                className="inline-edit-input"
                autoFocus
                disabled={isUpdating}
              />
              <div className="inline-edit-actions">
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleUpdatePacing();
                  }}
                  disabled={!editingPacingName.trim() || isUpdating}
                  className="inline-edit-btn save-btn"
                  title="Save"
                >
                  {isUpdating ? '⟳' : '✓'}
                </button>
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCancelEdit();
                  }}
                  disabled={isUpdating}
                  className="inline-edit-btn cancel-btn"
                  title="Cancel"
                >
                  ✕
                </button>
              </div>
            </div>
          ) : (
            <div className="campaign-name-container">
              <span className="campaign-name-text" title="Pacing schedule name">
                {schedule.name}
              </span>
              <div className="campaign-name-actions">
                <button
                  className="edit-name-btn"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleEditPacing(schedule);
                  }}
                  title="Edit pacing name"
                >
                  ✏️
                </button>
              </div>
            </div>
          )}
        </div>
      )
    },
    {
      key: 'pacingType',
      label: 'Type',
      width: '90px',
      sortable: true,
      className: 'type-info',
      render: (schedule) => (
        <span className={`pacing-type-badge ${schedule.pacingType === 'auto' ? 'auto' : 'manual'}`}>
          {schedule.pacingType === 'auto' ? 'Auto' : 'Manual'}
        </span>
      )
    },
    {
      key: 'platform',
      label: 'Platform',
      width: '120px',
      sortable: true,
      className: 'platform-info',
      render: (schedule) => getPlatformName(schedule.platform)
    },
    {
      key: 'status',
      label: 'Status',
      width: '110px',
      sortable: true,
      className: 'status-info',
      render: (schedule) => <StatusBadge status={schedule.status} />
    },
    {
      key: 'progress',
      label: 'Progress',
      width: '170px',
      render: (schedule) => {
        const total = schedule.totalCampaigns || 0;
        const success = schedule.successfulCampaigns || 0;
        const failed = schedule.failedCampaigns || 0;

        return (
          <div className="progress-cell">
            <span className="progress-numbers">{success}/{total}</span>
            {failed > 0 && <span className="failed-count">({failed} failed)</span>}
          </div>
        );
      }
    },
    {
      key: 'folder_name',
      label: 'Folder',
      width: '130px',
      sortable: true,
      className: 'folder-info',
      render: (schedule) => (
        <span className="folder-info">
          <FontAwesomeIcon icon={faFolder} />
          {schedule.folderName || 'No folder'}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Created',
      width: '170px',
      sortable: true,
      className: 'created-date',
      render: (schedule) => formatDate(schedule)
    },
    createActionColumn(null, dropdownRefs, openDropdownId, toggleDropdown)
  ];

  // Dropdown Menu Component
  const DropdownMenu = ({ schedule, onClose }) => {
    if (!schedule) return null;

    const handlePin = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      try {
        // Use correct endpoint based on pacing type
        const endpoint = schedule.pacingType === 'auto'
          ? `/api/v2/bulk-auto-pacing/${schedule.id}`
          : `/api/v2/pacing/${schedule.id}`;

        const response = await api.patch(getApiUrl(endpoint), {
          is_pinned: !schedule.is_pinned
        }, {
          params: {
            accountId,
            userId: user?.userId || user?.id
          }
        });

        if (response.status === 200) {
          // Update the schedule's pin status
          const updatedSchedules = pacingSchedules.map(s =>
            s.id === schedule.id ? { ...s, is_pinned: !s.is_pinned } : s
          );

          // Sort schedules with pinned items first, then by current sort criteria
          const sortedSchedules = updatedSchedules.sort((a, b) => {
            // Pinned schedules first
            if (a.is_pinned && !b.is_pinned) return -1;
            if (!a.is_pinned && b.is_pinned) return 1;

            // Within same pin status, sort by current sort criteria
            let comparison = 0;

            if (sortBy === 'name') {
              comparison = a.name.localeCompare(b.name);
            } else if (sortBy === 'status') {
              comparison = (a.status || '').localeCompare(b.status || '');
            } else if (sortBy === 'platform') {
              comparison = (a.platform || '').localeCompare(b.platform || '');
            } else if (sortBy === 'folder_name') {
              comparison = (a.folderName || '').localeCompare(b.folderName || '');
            } else if (sortBy === 'created_at') {
              comparison = new Date(a.createdAt) - new Date(b.createdAt);
            }

            return sortOrder === 'asc' ? comparison : -comparison;
          });

          setPacingSchedules(sortedSchedules);
        }
      } catch (error) {
        console.error('Error updating pacing schedule pin status:', error);
        setError('Failed to update pacing schedule. Please try again.');
      }
      onClose();
    };

    const handleDelete = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (window.confirm(`Are you sure you want to delete "${schedule.name}"?`)) {
        try {
          // Use correct endpoint based on pacing type
          const endpoint = schedule.pacingType === 'auto'
            ? `/api/v2/bulk-auto-pacing/${schedule.id}`
            : `/api/v2/pacing/${schedule.id}`;

          await api.delete(getApiUrl(endpoint), {
            params: {
              accountId,
              userId: user?.userId || user?.id
            }
          });
          setPacingSchedules(prev => prev.filter(s => s.id !== schedule.id));
          setPagination(prev => ({
            ...prev,
            totalItems: Math.max(0, prev.totalItems - 1)
          }));
        } catch (error) {
          console.error('Error deleting pacing schedule:', error);
          setError('Failed to delete pacing schedule. Please try again.');
        }
      }
      onClose();
    };

    return (
      <div
        className="sidebar-dropdown-menu"
        style={{
          top: `${dropdownPosition.top}px`,
          left: `${dropdownPosition.left}px`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onMouseDown={handlePin}
          className="sidebar-dropdown-item"
        >
          <FontAwesomeIcon icon={faThumbtack} />
          {schedule.is_pinned ? 'Unpin' : 'Pin'}
        </button>
        <button
          onMouseDown={handleDelete}
          className="sidebar-dropdown-item delete"
        >
          <FontAwesomeIcon icon={faTrash} />
          Delete
        </button>
      </div>
    );
  };

  if (!accountId) {
    return (
      <div className="page-container">
        <div className="page-error">
          <p>Please select an account to view pacing schedules.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="header-left">
          {selectedFolder && (
            <div className="breadcrumb-nav">
              <button
                onClick={() => handleFolderSelect(null)}
                className="breadcrumb-back-btn"
              >
                <FontAwesomeIcon icon={faArrowLeft} />
                All Folders
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-current">{getFolderDisplayLabel(selectedFolder)}</span>
            </div>
          )}
          <h1>
            Pacing
            {selectedFolder && <span className="folder-context">in {getFolderDisplayLabel(selectedFolder)}</span>}
            <span className="count-badge">{pagination.totalItems || pacingSchedules.length}</span>
          </h1>
          <p>
            {selectedFolder
              ? `Pacing schedules in the "${getFolderDisplayLabel(selectedFolder)}" folder`
              : 'Manage your campaign pacing schedules'
            }
          </p>
        </div>
        <div className="header-right">
          {/* Folder Filter */}
          <div className="folder-filter-container">
            <button
              type="button"
              className={`folder-filter-button ${showFolderDropdown ? 'active' : ''}`}
              onClick={() => setShowFolderDropdown(!showFolderDropdown)}
              disabled={foldersLoading}
            >
              <FontAwesomeIcon icon={faFolder} />
              <span>
                {selectedFolder ? getFolderDisplayLabel(selectedFolder) : 'All Folders'}
              </span>
              <FontAwesomeIcon
                icon={showFolderDropdown ? faChevronUp : faChevronDown}
                className="dropdown-arrow"
              />
            </button>

            {showFolderDropdown && (
              <div className="folder-filter-dropdown">
                <button
                  type="button"
                  className={`folder-filter-option ${!selectedFolder ? 'selected' : ''}`}
                  onClick={() => handleFolderSelect(null)}
                >
                  <FontAwesomeIcon icon={faFolder} />
                  <span>All Folders</span>
                </button>

                <div className="folder-filter-divider" />

                {folders.map(folder => (
                  <button
                    key={folder.id}
                    type="button"
                    className={`folder-filter-option ${selectedFolder?.id === folder.id ? 'selected' : ''}`}
                    onClick={() => handleFolderSelect(folder)}
                  >
                    <FontAwesomeIcon icon={faFolder} />
                    <span>{getFolderDisplayLabel(folder)}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="search-box">
            <FontAwesomeIcon icon={faSearch} className="search-icon" />
            <input
              type="text"
              placeholder="Search pacing schedules..."
              value={searchTerm}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          <button
            className="btn btn-primary"
            onClick={() => navigate('/pacing/new')}
          >
            <FontAwesomeIcon icon={faPlus} />
            Create Pacing
          </button>
        </div>
      </div>

      {error && (
        <div className="page-error">
          <p>{error}</p>
        </div>
      )}

      <DataTable
        data={pacingSchedules}
        columns={columns}
        pagination={pagination}
        onSort={requestSort}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onRowClick={(schedule, e) => {
          if (e.target.closest('.dropdown-container') ||
              e.target.closest('.sidebar-action-button')) {
            return;
          }
          // Navigate based on pacing type
          if (schedule.pacingType === 'auto') {
            navigate(`/pacing/bulk/${schedule.id}`);
          } else {
            navigate(`/pacing/${schedule.id}`);
          }
        }}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isLoading}
        emptyMessage={
          <div className="empty-state">
            <FontAwesomeIcon icon={faClock} className="empty-state-icon" />
            <h3>No pacing schedules yet</h3>
            <p>Create your first pacing schedule to get started</p>
          </div>
        }
        className="pacing-table"
        storageKey="pacing"
      />

      {/* Dropdown Menu */}
      {openDropdownId && (
        <DropdownMenu
          schedule={pacingSchedules.find(s => s.id === openDropdownId)}
          onClose={() => setOpenDropdownId(null)}
        />
      )}
    </div>
  );
};

export default PacingPage;

